import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { fade, makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import ArchiveIcon from "@material-ui/icons/Archive";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import EditIcon from "@material-ui/icons/Edit";
import PublishIcon from "@material-ui/icons/Publish";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import SmartPromptEditor from "../SmartPromptEditor";
import {
  agentTemplateFormToRequest,
  agentTemplateToForm,
  EMPTY_AGENT_TEMPLATE_FORM,
} from "../../helpers/agentTemplateForm";

const useStyles = makeStyles((theme) => ({
  root: { width: "100%", minWidth: 0 },
  stack: { display: "flex", flexDirection: "column", gap: theme.spacing(1.5) },
  hero: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: { alignItems: "stretch", flexDirection: "column" },
  },
  heroTitle: { fontWeight: 600 },
  filters: {
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableContainer: { maxHeight: "62vh", ...theme.scrollbarStyles },
  empty: { padding: theme.spacing(5, 2), textAlign: "center" },
  actions: { display: "flex", gap: theme.spacing(0.5), flexWrap: "wrap" },
  dialogPaper: { borderRadius: 18 },
  dialogIntro: {
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    background: fade(theme.palette.info.light, 0.12),
    border: `1px solid ${fade(theme.palette.info.main, 0.24)}`,
  },
  section: { marginTop: theme.spacing(2), marginBottom: theme.spacing(1), fontWeight: 700 },
  jsonField: { fontFamily: "monospace" },
  versions: {
    display: "flex",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
}));

const statusLabel = { draft: "Rascunho", published: "Publicado", archived: "Arquivado" };
const statusColor = { draft: "default", published: "primary", archived: "secondary" };

const AgentTemplatesManager = () => {
  const classes = useStyles();
  const [templates, setTemplates] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [searchParam, setSearchParam] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_AGENT_TEMPLATE_FORM });
  const [sampleValues, setSampleValues] = useState({});
  let variables = [];
  try { variables = JSON.parse(form.variables); } catch (_) { /* The JSON field preserves invalid drafts for correction. */ }

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/agent-templates", {
        params: {
          searchParam,
          status: status || undefined,
          category: category || undefined,
          pageNumber: page + 1,
          pageSize: rowsPerPage,
        },
      });
      setTemplates(data.templates || []);
      setCount(data.count || 0);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, [category, page, rowsPerPage, searchParam, status]);

  useEffect(() => {
    const timer = setTimeout(loadTemplates, 250);
    return () => clearTimeout(timer);
  }, [loadTemplates]);

  const changeForm = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const openCreate = () => {
    setSampleValues({});
    setSelected(null);
    setForm({ ...EMPTY_AGENT_TEMPLATE_FORM });
    setOpen(true);
  };

  const openEdit = async (template) => {
    setSaving(true);
    try {
      const { data } = await api.get(`/admin/agent-templates/${template.id}`);
      setSelected(data);
      setSampleValues({});
      setForm(agentTemplateToForm(data));
      setOpen(true);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const request = agentTemplateFormToRequest(form);
      const { data } = selected
        ? await api.put(`/admin/agent-templates/${selected.id}`, {
            expectedRevision: selected.revision,
            content: request.content,
          })
        : await api.post("/admin/agent-templates", request);
      const { data: detail } = await api.get(`/admin/agent-templates/${data.id}`);
      setSelected(detail);
      setForm(agentTemplateToForm(detail));
      toast.success(selected ? "Nova revisão de rascunho salva." : "Template criado como rascunho.");
      await loadTemplates();
    } catch (error) {
      if (error?.response) toastError(error);
      else toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const validate = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/agent-templates/${selected.id}/validate`);
      const failed = (data.checks || []).filter((item) => !item.passed);
      if (data.valid) toast.success(`Revisão ${data.version} validada com sucesso.`);
      else toast.error(failed.map((item) => item.message).join(" "));
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const mutate = async (template, action) => {
    const verb = action === "publish" ? "publicar" : "arquivar";
    if (!window.confirm(`Deseja ${verb} o template “${template.slug}”?`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(
        `/admin/agent-templates/${template.id}/${action}`,
        { expectedRevision: template.revision }
      );
      toast.success(action === "publish" ? "Template publicado." : "Template arquivado.");
      if (selected?.id === data.id) {
        const detail = await api.get(`/admin/agent-templates/${data.id}`);
        setSelected(detail.data);
        setForm(agentTemplateToForm(detail.data));
      }
      await loadTemplates();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.stack}>
        <div className={classes.hero}>
          <div>
            <Typography variant="h6" className={classes.heroTitle}>Templates oficiais de agentes</Typography>
            <Typography variant="body2" color="textSecondary">
              Publique pontos de partida seguros para todos os tenants. Cada publicação cria uma revisão imutável.
            </Typography>
          </div>
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Novo template
          </Button>
        </div>

        <Paper className={classes.filters}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField fullWidth label="Buscar por nome, finalidade ou slug" value={searchParam}
                onChange={(event) => { setSearchParam(event.target.value); setPage(0); }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField select fullWidth label="Status" value={status}
                onChange={(event) => { setStatus(event.target.value); setPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="draft">Rascunho</MenuItem>
                <MenuItem value="published">Publicado</MenuItem>
                <MenuItem value="archived">Arquivado</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth label="Categoria" value={category}
                onChange={(event) => { setCategory(event.target.value); setPage(0); }} />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button aria-label="Atualizar" onClick={loadTemplates}><RefreshIcon /></Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper className={classes.tablePaper}>
          {loading && <LinearProgress />}
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader size="small">
              <TableHead><TableRow>
                <TableCell>Template</TableCell><TableCell>Categoria</TableCell>
                <TableCell>Status</TableCell><TableCell>Revisões</TableCell><TableCell>Ações</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {!loading && templates.length === 0 && (
                  <TableRow><TableCell colSpan={5} className={classes.empty}>
                    <Typography color="textSecondary">Nenhum template encontrado.</Typography>
                  </TableCell></TableRow>
                )}
                {templates.map((template) => {
                  const version = template.draftVersion || template.publishedVersion || {};
                  return <TableRow key={template.id} hover>
                    <TableCell><Typography variant="subtitle2">{version.name || template.slug}</Typography>
                      <Typography variant="caption" color="textSecondary">{template.slug}</Typography></TableCell>
                    <TableCell>{version.category || "—"}</TableCell>
                    <TableCell><Chip size="small" color={statusColor[template.status]}
                      label={statusLabel[template.status] || template.status} /></TableCell>
                    <TableCell>
                      {template.publishedVersion && <Chip size="small" label={`Publicada v${template.publishedVersion.version}`} />}{" "}
                      {template.draftVersion && <Chip size="small" variant="outlined" label={`Rascunho v${template.draftVersion.version}`} />}
                    </TableCell>
                    <TableCell><div className={classes.actions}>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(template)}>Editar</Button>
                      {template.draftVersion && template.status !== "archived" &&
                        <Button size="small" color="primary" startIcon={<PublishIcon />}
                          onClick={() => mutate(template, "publish")}>Publicar</Button>}
                      {template.status !== "archived" &&
                        <Button size="small" startIcon={<ArchiveIcon />}
                          onClick={() => mutate(template, "archive")}>Arquivar</Button>}
                    </div></TableCell>
                  </TableRow>;
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={count} page={page} rowsPerPage={rowsPerPage}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
            rowsPerPageOptions={[12, 25, 50]} labelRowsPerPage="Por página" />
        </Paper>
      </div>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="md" fullWidth
        classes={{ paper: classes.dialogPaper }}>
        <DialogTitle>{selected ? `Editar ${selected.slug}` : "Novo template oficial"}</DialogTitle>
        <DialogContent>
          <Box className={classes.dialogIntro}>
            <Typography variant="body2">
              Não inclua credenciais, IDs de tenant, filas, conexões ou outras referências operacionais. O template deve funcionar para qualquer tenant.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth required label="Slug" value={form.slug}
              disabled={Boolean(selected)} onChange={changeForm("slug")} helperText="Identificador único, por exemplo: recepcao-triagem" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth required label="Nome" value={form.name} onChange={changeForm("name")} /></Grid>
            <Grid item xs={12}><TextField fullWidth required multiline rows={2} label="Finalidade"
              value={form.purpose} onChange={changeForm("purpose")} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth required label="Categoria" value={form.category} onChange={changeForm("category")} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth required label="Idioma" value={form.language} onChange={changeForm("language")} /></Grid>
            <Grid item xs={12} sm={4}><TextField select fullWidth label="Uso de conhecimento" value={form.knowledgeRequirement}
              onChange={changeForm("knowledgeRequirement")}>
              <MenuItem value="none">Não utiliza</MenuItem><MenuItem value="optional">Opcional</MenuItem><MenuItem value="required">Obrigatório</MenuItem>
            </TextField></Grid>
            <Grid item xs={12}><SmartPromptEditor admin disabled={saving} companyName="Empresa de exemplo"
              value={{ version: 1, text: form.instructions, variables: Array.isArray(variables) ? variables : [], values: sampleValues }}
              onChange={document => { setForm(current => ({ ...current, instructions: document.text, variables: JSON.stringify(document.variables, null, 2) })); setSampleValues(document.values); }} /></Grid>
          </Grid>

          <Typography className={classes.section}>Configuração estruturada</Typography>
          <Grid container spacing={2}>
            {[
              ["variables", "Variáveis (lista JSON)", 7],
              ["examples", "Cenários de exemplo (lista JSON)", 7],
              ["limits", "Limites (objeto JSON com do e doNot)", 6],
              ["recommendations", "Recomendações de execução (objeto JSON)", 6],
            ].map(([field, label, rows]) => (
              <Grid item xs={12} md={6} key={field}><TextField fullWidth multiline rows={rows} label={label}
                value={form[field]} onChange={changeForm(field)} InputProps={{ classes: { input: classes.jsonField } }} /></Grid>
            ))}
          </Grid>

          {selected?.versions?.length > 0 && <>
            <Typography className={classes.section}>Histórico imutável</Typography>
            <div className={classes.versions}>{selected.versions.map((version) =>
              <Chip key={version.id} size="small" variant="outlined"
                label={`v${version.version} · ${statusLabel[version.status] || version.status}`} />)}</div>
          </>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Fechar</Button>
          {selected && <Button startIcon={<CheckCircleOutlineIcon />} onClick={validate} disabled={saving}>Validar revisão</Button>}
          {selected?.draftVersion && selected.status !== "archived" &&
            <Button color="primary" startIcon={<PublishIcon />} onClick={() => mutate(selected, "publish")} disabled={saving}>Publicar</Button>}
          <Button color="primary" variant="contained" onClick={save} disabled={saving || selected?.status === "archived"}>
            {saving ? "Salvando..." : "Salvar rascunho"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AgentTemplatesManager;
